/**
 * NOTIFICATION CENTER
 *
 * A single bell that replaces per-page polling loops. The context manages
 * the in-memory feed; the bell component provides the dropdown UI.
 *
 * Notifications can be fed from:
 *  - REST: `fetchNotifications()` polls `/api/v1/notifications` once on
 *    mount (backs off silently if the endpoint doesn't exist — this is
 *    used while the WS fanout is being built out).
 *  - WebSocket: pipe `{type:"notification", ...}` messages through the
 *    `pushNotification` method from this context. App.js already handles
 *    the socket; it just forwards messages.
 *  - Local: any page can call `pushNotification(...)` directly to drop
 *    an event into the feed (useful for optimistic updates).
 *
 * Shape (all fields optional except `id` + `category`):
 *   {
 *     id, category, title, body, href, unread, created_at,
 *     actor: { name, avatar }
 *   }
 *
 * Categories: "message" | "job" | "proposal" | "contract" | "payment" | "system"
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { Link } from 'react-router-dom';
import { buildApiUrl, authedFetch } from '../../lib/utils';
import { formatLastSeen } from '../presence/PresenceContext';

const NotificationContext = createContext({
  items: [],
  unread: 0,
  pushNotification: () => {},
  markAllRead: () => {},
  markRead: () => {},
  dismiss: () => {},
  refresh: () => {},
});

const STORAGE_KEY = 'cbp.notifications.v1';
const MAX_ITEMS = 100;

const readStoredItems = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeStoredItems = (items) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(items.slice(0, MAX_ITEMS))
    );
  } catch {
    // ignore quota issues
  }
};

export const NotificationProvider = ({ children, isLoggedIn }) => {
  const [items, setItems] = useState(() => readStoredItems());
  const hasFetchedRef = useRef(false);

  // Persist whenever items change.
  useEffect(() => {
    writeStoredItems(items);
  }, [items]);

  const pushNotification = useCallback((next) => {
    if (!next) return;
    const normalized = {
      id:
        next.id ||
        `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      category: next.category || 'system',
      title: next.title || 'Notification',
      body: next.body || '',
      href: next.href || null,
      unread: next.unread !== false,
      created_at: next.created_at || new Date().toISOString(),
      actor: next.actor || null,
    };
    setItems((prev) => {
      // Dedupe by id.
      const filtered = prev.filter((i) => i.id !== normalized.id);
      return [normalized, ...filtered].slice(0, MAX_ITEMS);
    });
  }, []);

  const markAllRead = useCallback(() => {
    setItems((prev) => prev.map((i) => ({ ...i, unread: false })));
  }, []);

  const markRead = useCallback((id) => {
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, unread: false } : i))
    );
  }, []);

  const dismiss = useCallback((id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const refresh = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const data = await authedFetch(buildApiUrl('/api/v1/notifications'));
      const list = Array.isArray(data) ? data : data?.items || [];
      if (list.length === 0) return;
      list.forEach((raw) => pushNotification(raw));
    } catch {
      // endpoint may not exist yet; fail silently so polling can be
      // replaced without breaking the UI.
    }
  }, [isLoggedIn, pushNotification]);

  // Try fetching once after login.
  useEffect(() => {
    if (isLoggedIn && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      refresh();
    }
    if (!isLoggedIn) hasFetchedRef.current = false;
  }, [isLoggedIn, refresh]);

  const unread = useMemo(
    () => items.reduce((n, i) => n + (i.unread ? 1 : 0), 0),
    [items]
  );

  const value = useMemo(
    () => ({
      items,
      unread,
      pushNotification,
      markAllRead,
      markRead,
      dismiss,
      refresh,
    }),
    [items, unread, pushNotification, markAllRead, markRead, dismiss, refresh]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);

// ---------------------------------------------------------------------------
// Bell UI
// ---------------------------------------------------------------------------

const CATEGORY_META = {
  message: { icon: '💬', label: 'Messages', color: 'text-sky-600 bg-sky-50' },
  job: { icon: '📋', label: 'Jobs', color: 'text-indigo-600 bg-indigo-50' },
  proposal: {
    icon: '📝',
    label: 'Proposals',
    color: 'text-violet-600 bg-violet-50',
  },
  contract: {
    icon: '📑',
    label: 'Contracts',
    color: 'text-amber-600 bg-amber-50',
  },
  payment: {
    icon: '💰',
    label: 'Payments',
    color: 'text-emerald-600 bg-emerald-50',
  },
  system: { icon: '🔔', label: 'System', color: 'text-slate-600 bg-slate-50' },
};

export const NotificationBell = ({ className = '' }) => {
  const { items, unread, markAllRead, markRead, dismiss } = useNotifications();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Close when clicking outside.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div
      className={`relative ${className}`}
      ref={containerRef}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="relative flex items-center justify-center w-10 h-10 rounded-full text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo focus-visible:ring-offset-2 transition-colors"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-1rem)] bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <h2 className="font-semibold text-slate-900 text-sm">
              Notifications
            </h2>
            <button
              type="button"
              onClick={markAllRead}
              disabled={unread === 0}
              className="text-xs font-medium text-indigo hover:underline disabled:opacity-40 disabled:no-underline"
            >
              Mark all read
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-slate-500 text-sm">
                You're all caught up.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {items.map((item) => {
                  const meta =
                    CATEGORY_META[item.category] || CATEGORY_META.system;
                  const onActivate = () => {
                    markRead(item.id);
                    setOpen(false);
                  };
                  const content = (
                    <div className="flex items-start gap-3 px-4 py-3">
                      <span
                        className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-base ${meta.color}`}
                        aria-hidden="true"
                      >
                        {meta.icon}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none ${meta.color}`}
                          >
                            {meta.label}
                          </span>
                          {item.unread && (
                            <span
                              aria-label="Unread"
                              className="flex-shrink-0 w-2 h-2 bg-indigo rounded-full"
                            />
                          )}
                        </div>
                        <p
                          className={`text-sm truncate ${item.unread ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}
                        >
                          {item.title}
                        </p>
                        {item.body && (
                          <p className="text-xs text-slate-600 line-clamp-2 mt-0.5">
                            {item.body}
                          </p>
                        )}
                        <p className="text-[11px] text-slate-400 mt-1">
                          {formatLastSeen(item.created_at).replace(
                            'Last seen ',
                            ''
                          )}
                        </p>
                      </div>
                      <button
                        type="button"
                        aria-label="Dismiss notification"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          dismiss(item.id);
                        }}
                        className="flex-shrink-0 text-slate-300 hover:text-slate-500 text-lg leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo rounded"
                      >
                        ×
                      </button>
                    </div>
                  );
                  return (
                    <li key={item.id}>
                      {item.href ? (
                        <Link
                          to={item.href}
                          onClick={onActivate}
                          className={`block hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50 ${item.unread ? 'bg-indigo/5' : ''}`}
                        >
                          {content}
                        </Link>
                      ) : (
                        <button
                          type="button"
                          onClick={onActivate}
                          className={`block w-full text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50 ${item.unread ? 'bg-indigo/5' : ''}`}
                        >
                          {content}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
            <Link
              to="/notification-preferences"
              onClick={() => setOpen(false)}
              className="text-xs text-slate-600 hover:text-indigo focus-visible:outline-none focus-visible:underline"
            >
              Notification preferences →
            </Link>
          </div> */}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
